/**
 * Authentication Context
 * Handles user authentication state and operations
 *
 * S8: Stores both access_token and refresh_token in localStorage.
 * The silent refresh logic lives in the API interceptor (api/index.js),
 * not here — this context just manages storage and user state.
 *
 * Place at: src/context/AuthContext.jsx
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Extract a human-readable error message from any error shape.
 * Handles: { error: "..." }, { msg: "..." }, { message: "..." }, string, etc.
 */
function extractErrorMessage(err, fallback = 'Something went wrong') {
  // Axios error with response data
  const data = err?.response?.data;
  if (data) {
    if (typeof data === 'string') return data;
    if (data.error) return data.error;
    if (data.msg) return data.msg;
    if (data.message) return data.message;
  }
  // Plain Error object
  if (err?.message && err.message !== 'Request failed with status code 500') {
    return err.message;
  }
  return fallback;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('red_social_token');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await authAPI.verify();
        setUser(response.data.user);
      } catch (err) {
        console.error('Token verification failed:', err);
        // Don't clear tokens here — the interceptor's refresh logic
        // may recover the session. Only clear if verify itself wasn't
        // retried (which means refresh also failed).
        localStorage.removeItem('red_social_token');
        localStorage.removeItem('red_social_refresh_token');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const response = await authAPI.login(email, password);
      localStorage.setItem('red_social_token', response.data.access_token);
      // S8: Store refresh token if present
      if (response.data.refresh_token) {
        localStorage.setItem('red_social_refresh_token', response.data.refresh_token);
      }
      setUser(response.data.user);
      return response.data;
    } catch (err) {
      const message = extractErrorMessage(err, 'Login failed');
      setError(message);
      throw new Error(message);
    }
  }, []);

  const register = useCallback(async (email, password, username, dateOfBirth, turnstileToken, campusId) => {
    setError(null);
    try {
      const response = await authAPI.register(email, password, username, dateOfBirth, turnstileToken, campusId);
      localStorage.setItem('red_social_token', response.data.access_token);
      // S8: Store refresh token if present
      if (response.data.refresh_token) {
        localStorage.setItem('red_social_refresh_token', response.data.refresh_token);
      }
      setUser(response.data.user);
      return response.data;
    } catch (err) {
      const message = extractErrorMessage(err, 'Registration failed');
      setError(message);
      throw new Error(message);
    }
  }, []);

  const logout = useCallback(async () => {
    // S8: Revoke the refresh token on the server before clearing local state
    const refreshToken = localStorage.getItem('red_social_refresh_token');
    if (refreshToken) {
      try {
        await authAPI.logout(refreshToken);
      } catch (err) {
        // Don't block logout if the revocation call fails
        console.error('Refresh token revocation failed:', err);
      }
    }
    localStorage.removeItem('red_social_token');
    localStorage.removeItem('red_social_refresh_token');
    setUser(null);
    setError(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.me();
      setUser(response.data);
      return response.data;
    } catch (err) {
      console.error('Failed to refresh user:', err);
      return null;
    }
  }, []);

  const updateUser = useCallback((updates) => {
    setUser(prev => prev ? { ...prev, ...updates } : null);
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshUser,
    updateUser,
    clearError: () => setError(null)
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-zinc-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;